/**
 * Roubles typed by a person, minor units sent to the server — with no float in
 * between.
 *
 * `290,90` is not 29090 after a round trip through a JavaScript number: it is
 * 29089.999999999996, and `Math.round` only hides that for the cases someone
 * happened to test. Every conversion here is string arithmetic, so the digits
 * the owner typed are the digits the `bigint` column receives.
 *
 * PROVISIONAL: the brief says to use the converter the client-API agent
 * provides. That agent owns `src/api.ts`, which this file may not touch, so this
 * is the same job done locally. If a shared converter lands with the same
 * semantics — reject rather than round, comma and dot both accepted, no float —
 * delete this and import it; the tests to move across are the cases named below.
 */

/** A rouble figure as typed: digits, one separator, up to `decimals` after it. */
const TYPED_AMOUNT = /^(\d+)(?:[.,](\d*))?$/;

/**
 * Turns what the owner typed into the digit string the wire takes.
 *
 * Returns `null` for anything that is not a plain non-negative amount, and for
 * one more decimal place than the currency has — `290,905` is refused rather
 * than quietly rounded, because the owner is naming a price and a price that
 * silently becomes a different price is the one error nobody catches.
 *
 * Spaces are stripped first: `1 990` is how a person writes it, and the
 * non-breaking one is what a paste from anywhere else carries.
 */
export function toMinorUnits(input: string, decimals: number): string | null {
  const normalised = input.replace(/[\s\u00a0\u202f]/g, '');
  if (normalised === '') return null;

  const match = TYPED_AMOUNT.exec(normalised);
  if (!match) return null;

  const whole = match[1]!;
  const fraction = match[2] ?? '';
  if (fraction.length > decimals) return null;

  // Leading zeros are the owner's typing, not part of the number: `007` must
  // reach the column as `7`, and `0` must survive as `0`.
  return `${whole}${fraction.padEnd(decimals, '0')}`.replace(/^0+(?=\d)/, '');
}

/**
 * The reverse, for filling a field with an amount that already exists.
 *
 * Trailing zeros go: a field that opens reading `290,00` invites the owner to
 * delete two characters they never typed. The separator is a comma because that
 * is what the rest of this app prints — `formatMoney` runs `ru-RU`.
 */
export function fromMinorUnits(amount: string, decimals: number): string {
  if (!/^\d+$/.test(amount)) return '';
  if (decimals <= 0) return amount;

  const padded = amount.padStart(decimals + 1, '0');
  const whole = padded.slice(0, -decimals);
  const fraction = padded.slice(-decimals).replace(/0+$/, '');

  return fraction ? `${whole},${fraction}` : whole;
}

/**
 * Whether a field holds something that can be sent.
 *
 * Separate from {@link toMinorUnits} so an empty optional field — the old price,
 * which most products do not have — is not reported as a mistake.
 */
export const isTypedAmount = (input: string, decimals: number): boolean =>
  toMinorUnits(input, decimals) !== null;
